/**
 * 储蓄目标的**端侧纯逻辑**（I 域 · `DR-050` · 批 85）
 *
 * 依据：`I域PRD` `AC-I1.2`/`I1.6`/`I1.7` · `AC-I2.1`–`I2.5` · `AC-I3.1`–`I3.4` · `AC-I4.3`/`I4.5` ·
 * `DR-023`（端侧金额聚合 = 0）· 铁律 8（红=正/流入，取出用琥珀）· 铁律 63（可选键按需剔键）
 *
 * ## 🔴 本文件"一个数都不算"
 *
 * 进度百分比 / 还差多少 / 预计达成日 / 里程碑档位**全部来自服务端**
 * （`GoalView.progress` / `GoalView.pace` / `GoalView.progress.milestones`）。
 *
 * 会出现的"看起来像计算"的三处，其实都不是业务算术：
 * | 函数 | 它做的 |
 * |---|---|
 * | {@link percentTextOf} | **整数搬家**：`8600` → `"86.0%"`（只切字符串，不做除法） |
 * | {@link amountTextToCents} | 调共享白名单 `yuanTextToCentsOrNull()`（**唯一实现**在 `txn-draft.ts`） |
 * | {@link buildXxx} | 组装请求体（可选键按需剔键，不发 `undefined`） |
 *
 * ## 🔴 无排行（`AC-I3.4` 红线）
 *
 * 本文件**不提供任何"谁最多"的排序或占比**。成员贡献按服务端给的稳定序（`userId` 升序）
 * 原样拼文本 —— 按金额排就是排行榜的最小形态。
 */

import {
  AutoSaveMode,
  GoalMove,
  GoalPolicy,
  checkAutoSave,
  checkGoalShape,
  centsToYuanText,
  formatMoneyText,
  type Cents,
  type CreateGoalRequest,
  type GoalContributionSummary,
  type GoalMoveRequest,
  type GoalPaceView,
  type GoalView,
  type UpdateGoalRequest,
} from '@jiaying/shared';
import { yuanTextToCentsOrNull } from './txn-draft.js';

// ─────────────────────────── ① 展示文本 ───────────────────────────

/**
 * 万分之一整数 → 一位小数的百分比文本（`8600` → `"86.0%"`）。
 *
 * 🔴 **不做除法**：`/ 100` 在端侧被 ESLint 全封（`AC-P1.2`）。这里用**字符串切片**：
 * `8600` → 补到至少 3 位 `"8600"` → 末两位是小数 → `"86" + "." + "0"`。
 * ⚠️ 只保留**一位**小数（`86.05%` 会显示成 `86.0%`）—— 目标进度是情绪指标，
 * 两位小数会让人盯着 86.05 与 86.04 的差别，那不是这张卡要表达的东西。
 */
export function percentTextOf(progressE4: number): string {
  const negative = progressE4 < 0;
  const abs = negative ? -progressE4 : progressE4;
  const s = String(abs).padStart(3, '0');
  const intPart = s.slice(0, -2);
  const firstFrac = s.slice(-2, -1);
  return `${negative ? '-' : ''}${intPart}.${firstFrac}%`;
}

/** 卡面上的进度角标（超额时带 `+`，明确告诉用户"超了"） */
export function progressBadgeText(view: GoalView): string {
  const base = percentTextOf(view.progress.progressE4);
  return view.progress.overCents > 0 ? `${base} 已超额` : base;
}

/**
 * 进度条的 `value`（`LinearProgress` 只吃 `0–100` 的整数）。
 *
 * 🔴 **同样不做除法**：`Math.floor(e4 / 100)` 是端侧被禁的写法（`AC-P1.2`）。
 * 这里用字符串搬移取整数部分（`"8600"` → `"86"` → `86`），
 * 并把负数夹到 `0`、超额夹到 `100`（**只影响条形长度**，数字仍显示 130%，`AC-I1.6`）。
 */
export function barPercentOf(progressE4: number): number {
  const clamped = progressE4 < 0 ? 0 : progressE4;
  const s = String(clamped).padStart(3, '0');
  const intPart = Number(s.slice(0, -2));
  return intPart > 100 ? 100 : intPart;
}

/** 还差 / 已超额 的一句话（**金额走共享格式化器**，带币种） */
export function remainingText(view: GoalView): string {
  if (view.progress.reached) {
    return view.progress.overCents > 0
      ? `超出 ${formatMoneyText(view.progress.overCents, view.currency)}`
      : '已达成';
  }
  return `还差 ${formatMoneyText(view.progress.remainingCents, view.currency)}`;
}

/** 里程碑轨道的一格文案（`25%` / `50%` …）—— 档位来自服务端，端侧只加个百分号 */
export function milestoneText(tier: number): string {
  return `${String(tier)}%`;
}

/**
 * 节奏的**语气**（决定颜色，不决定文案 —— 文案由服务端下发，`AC-I4.3`）。
 *
 * 🔴 `behind` / `overdue` 用**琥珀**（`--c-notice`）而**不是红色**：
 * 红色在本项目 = 正/流入（铁律 8），用红色说"慢了"会让颜色语义彻底反转。
 */
export type PaceTone = 'ok' | 'notice' | 'muted';

export function paceToneOf(pace: GoalPaceView | null): PaceTone {
  if (pace === null) return 'muted';
  switch (pace.pace) {
    case 'reached':
    case 'onTrack':
      return 'ok';
    case 'behind':
    case 'overdue':
      return 'notice';
    case 'tooEarly':
      return 'muted';
    default:
      return 'muted';
  }
}

/** 攒/取动作的中文名（**唯一源在共享层**，此处只是给 UI 取用） */
export { GOAL_MOVE_LABEL } from '@jiaying/shared';

// ─────────────────────────── ② 目标表单草稿 ───────────────────────────

export interface GoalDraft {
  name: string;
  /** 元文本（**不预转分** —— 提交时才走 `yuanTextToCentsOrNull`） */
  amountText: string;
  /** `YYYY-MM-DD`；空串 = 没有截止日（提交时转 `null`） */
  dueDate: string;
  /** 关联账户 id；空串 = 不关联（首次攒钱时再绑定） */
  accountId: string;
  /**
   * 🆕 自动攒模式（`DR-052` D4）—— 默认 `off`（升级后既有目标不会被自动攒）。
   * 执行入口是 F3 发薪日工作流，不是本表单。
   */
  autoSaveMode: AutoSaveMode;
  /** 🆕 额度文本：`fixed` ⇒ 元；`percent` ⇒ 整数百分比（1–100）。`off` ⇒ 空串 */
  autoSaveText: string;
}

export function emptyGoalDraft(): GoalDraft {
  return { name: '', amountText: '', dueDate: '', accountId: '', autoSaveMode: AutoSaveMode.Off, autoSaveText: '' };
}

export function draftFromGoal(view: GoalView): GoalDraft {
  return {
    name: view.name,
    amountText: plainYuanText(view.targetCents),
    dueDate: view.dueDate ?? '',
    accountId: view.accountId ?? '',
    autoSaveMode: view.autoSaveMode,
    autoSaveText:
      view.autoSaveMode === AutoSaveMode.Fixed
        ? plainYuanText(view.autoSaveValue as Cents)
        : view.autoSaveMode === AutoSaveMode.Percent
          ? String(percentIntOfE4(view.autoSaveValue))
          : '',
  };
}

/**
 * 分 → **无符号、无千分位**的元文本（填进输入框用）。
 *
 * 🔴 不能直接用 `formatMoneyText()`：它带币种符号与千分位（`¥30,000.00`），
 * 塞进 `<input>` 后用户接着输入就会变成 `¥30,000.0012` 这种脏值。
 * ⇒ 走共享内核另一个定点函数 `centsToYuanText()`（纯数字 + 恒两位小数，**不是端侧除法**）。
 */
export function plainYuanText(cents: Cents): string {
  return centsToYuanText(cents);
}

/** 金额文本 → 整数分（`null` = 非法）—— 复用 `txn-draft` 的唯一实现，不另写一份 */
export function amountTextToCents(text: string): Cents | null {
  return yuanTextToCentsOrNull(text);
}

/**
 * E4（万分之一）→ **整数百分比**（`1000` → `10`）。
 *
 * 🔴 与 `percentTextOf` 同族：**只切字符串、不做除法**（`AC-P1.2` 在端侧封死了 `/`）。
 * ⚠️ 自动攒的比例在本批**只接受整数百分比**（1–100）：小数百分比会让"每期攒多少"出现
 * 一分二分的抖动，而 F3 的价值是"大致分一下"，不需要那种精度。
 */
export function percentIntOfE4(e4: number): number {
  const abs = e4 < 0 ? -e4 : e4;
  const s = String(abs).padStart(3, '0');
  const intPart = Number(s.slice(0, -2));
  return e4 < 0 ? -intPart : intPart;
}

/** 草稿的自动攒额度（**分**或 **E4**）—— `null` = 文本非法 */
export function autoSaveValueOfDraft(draft: GoalDraft): number | null {
  if (draft.autoSaveMode === AutoSaveMode.Off) return 0;
  const text = draft.autoSaveText.trim();
  if (text === '') return null;
  if (draft.autoSaveMode === AutoSaveMode.Fixed) {
    const cents = amountTextToCents(text);
    return cents === null ? null : (cents as number);
  }
  // percent：只接受 1–100 的整数
  if (!/^\d{1,3}$/.test(text)) return null;
  const pct = Number(text);
  if (pct < 1 || pct > 100) return null;
  return pct * 100;
}

export interface GoalDraftErrors {
  name?: string;
  target?: string;
  dueDate?: string;
  /** 🆕 自动攒额度（`DR-052` D4） */
  autoSave?: string;
}

/**
 * 草稿校验（**复用共享内核 `checkGoalShape()`** —— 端侧与服务端同一套规则）。
 *
 * 🔴 端侧只做**即时提示**；最终裁决在服务端（`DR-050`：端侧不预判服务端的业务规则）。
 */
export function goalDraftErrors(draft: GoalDraft): GoalDraftErrors {
  const cents = amountTextToCents(draft.amountText);
  const issues = checkGoalShape({
    name: draft.name,
    // 空文本先报"请填金额"，不让内核报"必须是整数分"（后者对用户没意义）
    targetCents: cents ?? -1,
    dueDate: draft.dueDate === '' ? null : draft.dueDate,
    accountId: draft.accountId === '' ? null : draft.accountId,
  });
  const out: GoalDraftErrors = {};
  for (const issue of issues) {
    if (issue.field === 'name') out.name = issue.message;
    if (issue.field === 'target') {
      out.target = draft.amountText.trim() === '' ? '请填写目标金额' : issue.message;
    }
    if (issue.field === 'dueDate') out.dueDate = issue.message;
  }

  // 🆕 自动攒：端侧只做「文本能不能解释成数」+ 复用内核的区间校验（不重写区间）
  if (draft.autoSaveMode !== AutoSaveMode.Off) {
    const value = autoSaveValueOfDraft(draft);
    if (value === null) {
      out.autoSave =
        draft.autoSaveMode === AutoSaveMode.Fixed
          ? '请填写每期攒多少钱（如 300 或 300.00）'
          : '请填写 1–100 的整数百分比';
    } else {
      const issues = checkAutoSave({ config: { mode: draft.autoSaveMode, value } });
      if (issues.length > 0) out.autoSave = issues.join('；');
    }
  }

  return out;
}

/** 建目标请求（🔴 可选键按需剔键 —— 铁律 63：不发 `undefined`，也不发空串） */
export function buildCreateGoalRequest(draft: GoalDraft): CreateGoalRequest {
  const target = amountTextToCents(draft.amountText) as number;
  const req: CreateGoalRequest = { name: draft.name.trim(), target };
  if (draft.dueDate !== '') req.dueDate = draft.dueDate;
  if (draft.accountId !== '') req.accountId = draft.accountId;
  // 🆕 自动攒（`DR-052` D4）：只在用户真的开了时才带（`off` 是服务端默认，不必发）
  const autoValue = autoSaveValueOfDraft(draft);
  if (draft.autoSaveMode !== AutoSaveMode.Off && autoValue !== null) {
    req.autoSaveMode = draft.autoSaveMode;
    req.autoSaveValue = autoValue;
  }
  return req;
}

/**
 * 改目标请求 —— 🔴 **只发改过的键**（`DR-050` D3：不发的键 = 不改）。
 *
 * 为什么不能整份发：`accountId` 一旦整份发出去，"换绑"这条硬规则就会被误触发；
 * `dueDate: null` 更是会**清空**用户没动过的截止日。
 */
export function buildUpdateGoalRequest(draft: GoalDraft, view: GoalView): UpdateGoalRequest {
  const req: UpdateGoalRequest = {};
  const name = draft.name.trim();
  if (name !== view.name) req.name = name;

  const target = amountTextToCents(draft.amountText);
  if (target !== null && (target as number) !== (view.targetCents as number)) req.target = target;

  const due = draft.dueDate === '' ? null : draft.dueDate;
  if (due !== view.dueDate) req.dueDate = due;

  const account = draft.accountId === '' ? null : draft.accountId;
  if (account !== view.accountId) req.accountId = account;

  // 🆕 自动攒（`DR-052` D4）：**两个一起发** —— 改成 `off` 时额度必须同时归 0
  const autoValue = autoSaveValueOfDraft(draft);
  if (
    autoValue !== null &&
    (draft.autoSaveMode !== view.autoSaveMode || autoValue !== view.autoSaveValue)
  ) {
    req.autoSaveMode = draft.autoSaveMode;
    req.autoSaveValue = autoValue;
  }

  return req;
}

/** 改目标请求是否为空（空 ⇒ 不发请求，直接关面板） */
export function isEmptyUpdate(req: UpdateGoalRequest): boolean {
  return Object.keys(req).length === 0;
}

// ─────────────────────────── ③ 攒 / 取草稿 ───────────────────────────

export interface GoalMoveDraft {
  move: GoalMove;
  amountText: string;
  date: string;
  /** 对手账户：攒 = 来源；取 = 去向 */
  counterAccountId: string;
  /** 目标还没绑账户时，攒钱要选"存到哪个账户"（`DR-050` D3） */
  hostAccountId: string;
  note: string;
}

export function emptyMoveDraft(move: GoalMove, today: string): GoalMoveDraft {
  return { move, amountText: '', date: today, counterAccountId: '', hostAccountId: '', note: '' };
}

export interface GoalMoveErrors {
  amount?: string;
  date?: string;
  counterAccountId?: string;
  hostAccountId?: string;
}

/**
 * 攒/取校验。
 *
 * 🔴 三条**只在端侧就能判**的（金额、日期、账户必选）；
 * 「两账户币种是否一致」「宿主是否存在」「余额够不够」留给服务端 —— 端侧没有那些数据。
 */
export function moveDraftErrors(draft: GoalMoveDraft, view: GoalView): GoalMoveErrors {
  const out: GoalMoveErrors = {};
  const cents = amountTextToCents(draft.amountText);
  if (cents === null) out.amount = '请填写金额（最多两位小数）';
  else if ((cents as number) <= 0) out.amount = '金额必须大于 0';
  if (draft.date.trim() === '') out.date = '请选择日期';
  if (draft.counterAccountId === '') out.counterAccountId = '请选择账户';
  if (draft.move === GoalMove.Save && view.accountId === null && draft.hostAccountId === '') {
    out.hostAccountId = '请选择存到哪个账户（选一次就记住了）';
  }
  return out;
}

/** 攒/取请求（🔴 可选键按需剔键；`hostAccountId` **只在首次绑定**时才发） */
export function buildMoveRequest(draft: GoalMoveDraft, view: GoalView): GoalMoveRequest {
  const req: GoalMoveRequest = {
    move: draft.move,
    amount: amountTextToCents(draft.amountText) as number,
    date: draft.date,
    counterAccountId: draft.counterAccountId,
  };
  if (draft.move === GoalMove.Save && view.accountId === null && draft.hostAccountId !== '') {
    req.hostAccountId = draft.hostAccountId;
  }
  const note = draft.note.trim();
  if (note !== '') req.note = note;
  return req;
}

// ─────────────────────────── ④ 删除与确认文案 ───────────────────────────

export interface GoalDeletePlan {
  /** 需要「放弃归属」二次确认（已有攒钱额） */
  needsForfeit: boolean;
  /** 二次确认文案（`AC-I4.5`：必须说清"腿留着、归属会丢"） */
  warning: string;
}

/**
 * 删除确认方案（`AC-I4.5` / E5）。
 *
 * 🔴 文案必须说清**两件事**，否则用户以为"删了就等于把钱收回来了"：
 * ① 流水（腿）**一条都不会删**（钱真的动过）；② 但"这笔是给这个目标攒的"会丢。
 */
export function goalDeletePlan(view: GoalView): GoalDeletePlan {
  if ((view.savedCents as number) <= 0) {
    return { needsForfeit: false, warning: '删除后该目标会消失；它还没有攒钱记录，不影响任何流水。' };
  }
  return {
    needsForfeit: true,
    warning:
      `这个目标里还有 ${formatMoneyText(view.savedCents, view.currency)} 的攒钱记录。`
      + '删除后这些流水**仍会留在账上**（钱确实动过），'
      + '但"这笔钱是给这个目标攒的"这条归属信息会丢失。',
  };
}

// ─────────────────────────── ⑤ 贡献合计 ───────────────────────────

/**
 * 成员贡献的一句话（原型：`我 ¥16,800 · 王琳 ¥9,000`）。
 *
 * 🔴 **严格按服务端给的顺序**（`userId` 升序）拼接，**绝不按金额重排**（`AC-I3.4` 红线）。
 * `meId` 只用来把"我"这个词替换掉自己的名字 —— 那是一次**查找**，不是排序。
 */
export function contributionsText(
  list: readonly GoalContributionSummary[],
  currency: string,
  meId: string,
): string {
  if (list.length === 0) return '还没有人攒过';
  return list
    .map((c) => {
      const who = c.userId === meId ? '我' : c.name;
      return `${who} ${formatMoneyText(c.netCents, currency)}`;
    })
    .join(' · ');
}

/** 庆祝卡文案（`AC-I3.7`：含分成员合计；`AC-I3.6`：**不含广告 / 导流**） */
export function celebrateLines(view: GoalView, meId: string): readonly string[] {
  const lines = [
    `「${view.name}」攒到了 ${formatMoneyText(view.targetCents, view.currency)}`,
    contributionsText(view.contributions, view.currency, meId),
  ];
  if (view.progress.overCents > 0) {
    lines.push(`还多攒了 ${formatMoneyText(view.progress.overCents, view.currency)}`);
  }
  // 🔴 刻意不加"分享到 xx"「下载 App」「关注公众号」之类 —— `AC-I3.6` 红线
  return lines;
}

/** 每档里程碑的说明（供无障碍 `aria-label`） */
export function milestoneAriaLabel(tier: number, reached: boolean): string {
  return `${milestoneText(tier)} 里程碑${reached ? '已点亮' : '未点亮'}`;
}

/** 目标数上限（与服务端同一常量 —— 端侧只用于提示，不做拦截） */
export const GOAL_MAX_PER_LEDGER = GoalPolicy.MaxGoalsPerLedger;
